import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { defaultFiveDaysAgo, today } from "../dates";
import { OrderStatus, useOrders } from "../../hooks/useOrders";
import useStatusTranslations from "../../hooks/useStatusTranslations";
import { useSnackbar } from "notistack";
import {
  cod,
  codByRecipient,
  customTag,
  deliveryAddress,
  deliveryCity,
  departureAddress,
  departureCity,
  description,
  estimatedCost,
  internationalTrackingNumber,
  orderNumber,
  otherCosts,
  places,
  printIcon,
  recipient,
  recipientPhone,
  registerNumber,
  returnParcelToSender,
  returnParcelToSenderStatus,
  returnParcelToSenderTrackNumber,
  shipmentStatus,
  totalServicesAmountByRecipient,
  totalServicesAmountBySender,
  withPlaces,
} from "../reusableOrdersColumns";
import { handlePrintOrderSticker } from "../handlePrint";
import { dateTimeColumnDef } from "../dateTimeColumnDef";
import { Typography } from "@mui/material";
import DataGridTable from "../dataGrid/DataGridTable";
import showApiErrorNotification from "../showApiErrorNotification";
import { validateOrders } from "../validateOrders";
import { PageTitle } from "../pageTitle/PageTitle";
import { DataGridWrapper } from "../dataGrid/DataGridWrapper";

function ArchivedOrders() {
  const { t } = useTranslation();
  const [selectedDates, setSelectedDates] = useState([defaultFiveDaysAgo, today]);

  const {
    orders,
    loading: loadingOrders,
    unarchiveOrders,
    fetchOrders,
  } = useOrders(OrderStatus.ARCHIVED, {
    startDate: selectedDates[0],
    endDate: selectedDates[1],
  });
  const { formatOrderStatus } = useStatusTranslations();
  const [selectedOrderTrackingCodes, setSelectedOrderTrackingCodes] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const validatedOrders = useMemo(() => validateOrders(orders, t), [orders, t]);

  const columns = useMemo(
    () => [
      orderNumber(t),
      printIcon(t, 80, (trackingCode) => handlePrintOrderSticker(trackingCode)),
      recipient(t),
      recipientPhone(t),
      deliveryCity(t),
      deliveryAddress(t),
      shipmentStatus(t, formatOrderStatus),
      {
        field: "orderDate",
        headerName: `${t("ordersTableHeaders.sendDate")}`,
        width: 180,
        align: "left",
        headerAlign: "left",
        ...dateTimeColumnDef,
      },
      customTag(t),
      internationalTrackingNumber(t),
      description(t),
      estimatedCost(t),
      cod(t),
      codByRecipient(t),
      otherCosts(t),
      departureCity(t),
      departureAddress(t),
      registerNumber(t, 150, false),
      withPlaces(t),
      places(t),
      returnParcelToSender(t),
      returnParcelToSenderTrackNumber(t),
      returnParcelToSenderStatus(t, formatOrderStatus),
      totalServicesAmountBySender(t),
      totalServicesAmountByRecipient(t),
    ],
    [t, formatOrderStatus],
  );

  const handlerUnarchiveOrders = async () => {
    try {
      const unarchivedOrders = await unarchiveOrders(selectedOrderTrackingCodes);
      if (unarchivedOrders) {
        enqueueSnackbar(
          selectedOrderTrackingCodes.length === 1
            ? t("alertsMessages.orderUnarchived")
            : t("alertsMessages.ordersUnarchived"),
          {
            variant: "success",
          },
        );
        fetchOrders({ startDate: selectedDates[0], endDate: selectedDates[1] });
      } else {
        enqueueSnackbar(t("alertsMessages.failedUnarchiveOrder"), {
          variant: "error",
        });
        console.error(`Error unarchive orders: ${selectedOrderTrackingCodes}`);
      }
    } catch (error) {
      showApiErrorNotification(t("alertsMessages.failedUnarchiveOrder"), error, enqueueSnackbar);
      console.error(`Error unarchive orders: ${selectedOrderTrackingCodes}`, error);
    }
  };

  const handleDateChange = useCallback(
    (dates) => {
      setSelectedDates(dates);
    },
    [setSelectedDates],
  );

  return (
    <DataGridWrapper>
      <DataGridTable
        title={<PageTitle title={t("pagesTitles.archive")} />}
        showCalendar={true}
        selectedDates={selectedDates}
        onDateChange={handleDateChange}
        rows={validatedOrders}
        autoHeight={false}
        showUnarchiveIcon={true}
        rowSelectionModel={selectedOrderTrackingCodes}
        onRowSelectionModelChange={setSelectedOrderTrackingCodes}
        onUnarchive={handlerUnarchiveOrders}
        disableIcons={selectedOrderTrackingCodes.length === 0}
        columns={columns}
        loading={loadingOrders}
        onRefresh={() => fetchOrders({ startDate: selectedDates[0], endDate: selectedDates[1] })}
        emptyTableText={
          <Typography className={"text-center pt-8"}>
            {t("ordersTableHeaders.noOrders")}
          </Typography>
        }
      />
    </DataGridWrapper>
  );
}

export default ArchivedOrders;
